import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";

function Products() {
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("id");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState(null);
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [titleError, setTitleError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [logoError, setLogoError] = useState("");
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableFrames, setAvailableFrames] = useState([]);
  const [sizesList, setSizesList] = useState([
    { value: "A4 - 8.3 x 11.7 inches", label: "A4 - 8.3 x 11.7 inches" },
    { value: "A5 - 5.8 x 8.3 inches", label: "A5 - 5.8 x 8.3 inches" },
    { value: "10 X 10 inches", label: "10 X 10 inches" },
    { value: "15 x 30 inches", label: "15 x 30 inches" },
  ]);
  const [framesList, setFramesList] = useState([
    { value: "WoodenFrame", label: "Wooden Frame" },
    { value: "AcrylicFrame", label: "Acrylic Frame" },
    { value: "CollageFrame", label: "Collage Frame" },
    { value: "DigitalFrame", label: "Digital Frame" },
    { value: "MetalFrame", label: "Metal Frame" },
    { value: "ShelfFrame", label: "Shelf Frame" },
    { value: "TabletopFrame", label: "Tabletop Frame" },
    { value: "FloatingFrame", label: "Floating Frame" },
    { value: "ModernFrame", label: "Modern Frame" },
  ]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormProcessing, setIsFormProcessing] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const validateForm = () => {
      setIsFormValid(
        title.trim() !== "" &&
          description.trim() !== "" &&
          (logo !== null || uploadedLogoUrl !== "")
      );
    };
    validateForm();
  }, [title, description, logo, uploadedLogoUrl]);

  useEffect(() => {
    if (productId) {
      fetchProductData(productId);
    }
  }, [productId]);

  const fetchProductData = (id) => {
    axios
      .get(`${process.env.REACT_APP_APIURL}products/list`, {
        params: { products_id: id },
      })
      .then((res) => {
        if (res.data.status === "success") {
          const product = res.data.result;
          setTitle(product.name);
          setDescription(product.description);
          setUploadedLogoUrl(product.logo);
          setAvailableSizes(
            product.size_list.map((size) => ({ value: size, label: size }))
          );
          setAvailableFrames(
            product.frame_list.map((frame) => ({ value: frame, label: frame }))
          );
        } else {
          setErrorMessage("Failed to fetch product data");
        }
      })
      .catch((err) => {
        console.error("Product fetch error:", err);
        setErrorMessage("Failed to fetch product data");
      });
  };

  const handleAddProducts = async (e) => {
    e.preventDefault();

    // Disable the submit button while processing the form
    setIsFormProcessing(true);

    let valid = true;
    // Your form validation logic goes here
    
    // If validation fails, set valid to false
    if (!valid) {
      setIsFormProcessing(false);
      return;
    }

    if (title.trim() === "") {
      setTitleError("Title is required");
      valid = false;
    } else {
      setTitleError("");
    }

    if (description.trim() === "") {
      setDescriptionError("Description is required");
      valid = false;
    } else {
      setDescriptionError("");
    }

    if (!logo && !uploadedLogoUrl) {
      setLogoError("Image is required");
      valid = false;
    } else {
      setLogoError("");
    }

    if (!valid) {
      setIsFormProcessing(false); // Reset processing state if validation fails
      return;
    }

    try {
      let logoUrl = uploadedLogoUrl;
      if (logo) {
        logoUrl = await fileUpload();
      }

      const apiUrl = productId
        ? `${process.env.REACT_APP_APIURL}products/update`
        : `${process.env.REACT_APP_APIURL}products/create`;

      const payload = {
        title: title,
        description: description,
        logo: logoUrl,
        size_list: availableSizes.map((size) => size.value).toString(),
        frame_list: availableFrames.map((frame) => frame.value).toString(),
        frame: availableFrames.length !== 0 ? "yes" : "no",
        product_id: productId ? productId : "",
      };

      axios
        .post(apiUrl, payload)
        .then((res) => {
          if (res.data.status === "success") {
            setTitle("");
            setDescription("");
            setLogo(null);
            setUploadedLogoUrl("");
            setAvailableSizes([]);
            setAvailableFrames([]);
            setErrorMessage("");
            setTitleError("");
            setDescriptionError("");
            setLogoError("");
            setIsFormValid(false);
            setSuccessMessage(
              productId
                ? "Product updated successfully!"
                : "Product added successfully!"
            );

            setIsFormProcessing(false); // Reset processing state after success

            setTimeout(() => {
              setSuccessMessage("");
              navigate("/list");
            }, 2000);
          } else {
            setErrorMessage("Failed to add/update product");
            setIsFormProcessing(false); // Reset processing state after failure
          }
        })
        .catch((err) => {
          console.error("Product creation/update error:", err);
          setErrorMessage("Failed to add/update product");
          setIsFormProcessing(false); // Reset processing state after failure
        });
    } catch (err) {
      console.error("File upload error:", err);
      setErrorMessage("File upload failed");
      setIsFormProcessing(false); // Reset processing state after failure
    }
  };

  const fileUpload = () => {
    const formData = new FormData();
    formData.append("file", logo);

    return axios
      .post(process.env.REACT_APP_APIURL + "file/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        if (res.data.status === "success") {
          setUploadedLogoUrl(res.data.result.files[0].file_url);
          return res.data.result.files[0].file_url;
        } else {
          throw new Error("File upload failed");
        }
      });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = function (event) {
      const img = new Image();
      img.src = event.target.result;

      img.onload = function () {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Set the desired width and height for the resized image
        const maxWidth = 800;
        const maxHeight = 600;

        let width = img.width;
        let height = img.height;

        // Calculate the new dimensions while maintaining the aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw the image onto the canvas with the new dimensions
        ctx.drawImage(img, 0, 0, width, height);

        // Convert the canvas content back to a data URL
        const resizedDataURL = canvas.toDataURL("image/jpeg");

        // Create a Blob object from the data URL with specified filename
        const blob = dataURItoBlob(resizedDataURL, file.name);

        // Set the resized image as the new logo
        setLogo(blob);
      };
    };

    reader.readAsDataURL(file);
  };

  // Function to convert data URI to Blob with specified filename
  const dataURItoBlob = (dataURI, filename) => {
    const byteString = atob(dataURI.split(",")[1]);
    const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    // Create a new Blob with the specified filename
    return new File([ab], filename, { type: mimeString });
  };

  const handleSizeChange = (selectedOptions) => {
    setAvailableSizes(selectedOptions);
  };

  const handleFrameChange = (selectedOptions) => {
    setAvailableFrames(selectedOptions);
  };

  useEffect(() => {
    const pathname = window.location.pathname;
    if (pathname === "/products") {
      console.log("Adding classes to the body.");
      document.body.classList.remove("login-page");
      document.body.classList.add("sidebar-mini");
    }
  }, []);

  return (
    <div className="wrapper">
      <header>
        <NavigationBar />
      </header>

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0 txt_lft">
                  {productId ? "Edit Product" : "Add Product"}
                </h1>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <form
                      className="form-horizontal"
                      onSubmit={handleAddProducts}
                    >
                      <div className="card-body">
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Title<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <input
                              type="text"
                              className="form-control"
                              value={title}
                              onChange={(e) => setTitle(e.target.value)}
                            />
                            {titleError && (
                              <div className="text-danger mt-1">
                                {titleError}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Description<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <textarea
                              className="form-control txtarea_hgt"
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                            />
                            {descriptionError && (
                              <div className="text-danger mt-1">
                                {descriptionError}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Available Size(s)<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <Select
                              isMulti
                              options={sizesList}
                              value={availableSizes}
                              onChange={handleSizeChange}
                              closeMenuOnSelect={false}
                            />
                          </div>
                        </div>

                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Available Frame(s)<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <Select
                              isMulti
                              options={framesList}
                              value={availableFrames}
                              onChange={handleFrameChange}
                              closeMenuOnSelect={false}
                            />
                          </div>
                        </div>

                        <div className="form-group row">
                          <label className="col-12 col-md-4 col-form-label">
                            Image<span className="f_red">*</span>
                          </label>
                          <div className="col-12 col-md-8">
                            <div className="position-relative">
                              <label className="btn-bs-file btn btn-primary">
                                Browse
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleFileChange}
                                />
                              </label>
                            </div>
                            {logoError && (
                              <div className="text-danger mt-1">
                                {logoError}
                              </div>
                            )}
                            {(logo || uploadedLogoUrl) && (
                              <div className="product_imgdiv mt-3">
                                <img
                                  src={
                                    logo
                                      ? URL.createObjectURL(logo)
                                      : uploadedLogoUrl
                                  }
                                  alt="Product"
                                  border={0}
                                />
                                <div
                                  className="product_imgdel"
                                  onClick={() => {
                                    setLogo(null);
                                    setUploadedLogoUrl("");
                                  }}
                                >
                                  <i className="fa-solid fa-xmark" />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="card-footer text-center">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={!isFormValid || isFormProcessing} // Disable button when form is processing
                        >
                          {isFormProcessing
                            ? "Processing..."
                            : productId
                            ? "Update"
                            : "Add"}{" "}
                          {/* Conditional button text */}
                        </button>
                      </div>
                    </form>
                    {errorMessage && (
                      <div className="alert alert-danger mt-3">
                        {errorMessage}
                      </div>
                    )}
                    {successMessage && (
                      <div className="alert alert-success mt-3">
                        {successMessage}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Products;
